import { TextField } from '@mui/material';
import { getWarehousesThunk, updateWarehouseThunk } from '@redux/features/warehouse.slice';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import Modal from 'Common/Components/Modal';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import { useFormik } from 'formik';
import React, { FC } from 'react';
import * as Yup from 'yup';

interface IProps {
    show: boolean;
    handleClose: () => void;
    warehouseId: number;
}

const UpdateWareHouseModal: FC<IProps> = ({ show, handleClose, warehouseId }) => {
    const { loading, warehouses } = useAppSelector(
        (state: RootState) => (state as any).warehouseState || { loading: false, warehouses: [] }
    );
    const current = (warehouses || []).find((w: any) => w?.id === warehouseId);
    const dispatch = useAppDispatch();

    const validationSchema = Yup.object({
        name: Yup.string().required('Name is required'),
        location: Yup.string().required('Location is required'),
        dunsNumber: Yup.string().required('DUNS Number is required'),
    });

    const handleSubmit = (values: any) => {
        dispatch(
            updateWarehouseThunk({
                id: warehouseId,
                name: values.name,
                location: values.location,
                dunsNumber: values.dunsNumber,
            } as any)
        ).then((res: any) => {
            if (res.meta.requestStatus === 'fulfilled') {
                dispatch(getWarehousesThunk());
                handleClose();
                formik.resetForm();
            }
        });
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: current?.name || '',
            location: current?.location || '',
            dunsNumber: current?.dunsNumber || '',
        },
        validationSchema,
        onSubmit: handleSubmit,
    });

    return (
        <Modal
            show={show}
            onHide={() => handleClose()}
            dialogClassName="w-full bg-white p-3 shadow rounded-md overflow-scroll md:w-[500px] md:max-h-[80vh]"
        >
            <Modal.Header>
                <Modal.Title className="text-lg font-semibold">Update Warehouse</Modal.Title>
            </Modal.Header>
            <Modal.Body className="my-5">
                <form onSubmit={formik.handleSubmit} className="flex flex-col gap-4">
                    <TextField
                        size="small"
                        fullWidth
                        id="name"
                        name="name"
                        label="Name"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.name && Boolean(formik.errors.name)}
                        helperText={formik.touched.name ? String(formik.errors.name || '') : ''}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="location"
                        name="location"
                        label="Location"
                        value={formik.values.location}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.location && Boolean(formik.errors.location)}
                        helperText={formik.touched.location ? String(formik.errors.location || '') : ''}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="dunsNumber"
                        name="dunsNumber"
                        label="DUNS Number"
                        value={formik.values.dunsNumber}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.dunsNumber && Boolean(formik.errors.dunsNumber)}
                        helperText={formik.touched.dunsNumber ? String(formik.errors.dunsNumber || '') : ''}
                    />
                </form>
            </Modal.Body>
            <Modal.Footer className="flex items-center justify-center gap-2 mt-5">
                <ButtonBase
                    innerText="Cancel"
                    className="bg-gray-500 hover:bg-gray-600 rounded border-none px-4 py-2 text-white"
                    onClick={() => {
                        handleClose();
                        formik.resetForm();
                    }}
                    disabled={loading}
                />
                <ButtonBase
                    innerText="Update Warehouse"
                    className="bg-yellow-600 hover:bg-yellow-800 rounded border-none px-4 py-2 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={formik.handleSubmit}
                    disabled={loading || !formik.values.name || !formik.values.location || !formik.values.dunsNumber}
                />
            </Modal.Footer>
        </Modal>
    );
};

export default UpdateWareHouseModal;
